import type { DesignSpec, SectionSpec, SectionType, FontPair, ColorScheme } from '../types/design-spec';
import type { ProjectConfig } from '../types/project';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function getBrandName(config: ProjectConfig): string {
  return config.brand.nameCn || config.brand.nameEn || config.displayName || 'Brand';
}

function getBrandDescription(config: ProjectConfig): string {
  return config.brand.description || config.brand.tagline || '';
}

function getFontConfig(fontPair: FontPair, designSpec: DesignSpec) {
  switch (fontPair) {
    case 'inter-noto':
      return {
        imports: "<link href='https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Noto+Sans+TC:wght@400;500;700&display=swap' rel='stylesheet'>",
        family: "'Inter', 'Noto Sans TC', system-ui, sans-serif",
      };
    case 'playfair-inter':
      return {
        imports: "<link href='https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@400;500;600;700&display=swap' rel='stylesheet'>",
        family: "'Playfair Display', 'Inter', system-ui, sans-serif",
      };
    case 'montserrat-open':
      return {
        imports: "<link href='https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&family=Open+Sans:wght@400;500;600;700&display=swap' rel='stylesheet'>",
        family: "'Montserrat', 'Open Sans', system-ui, sans-serif",
      };
    case 'noto-tc-only':
      return {
        imports: "<link href='https://fonts.googleapis.com/css2?family=Noto+Sans+TC:wght@400;500;700&display=swap' rel='stylesheet'>",
        family: "'Noto Sans TC', system-ui, sans-serif",
      };
    case 'custom':
      return {
        imports: "",
        family: `'${designSpec.typography.headingFont}', '${designSpec.typography.bodyFont}', system-ui, sans-serif`,
      };
    case 'system':
    default:
      return {
        imports: "",
        family: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      };
  }
}

function getColorSchemePreset(colorScheme: ColorScheme): Partial<DesignSpec['colors']> | null {
  switch (colorScheme) {
    case 'blue-professional':
      return { primary: '#1a56db', secondary: '#60a5fa', accent: '#f59e0b', background: '#ffffff', surface: '#f8fafc', text: '#1f2937', muted: '#6b7280' };
    case 'green-nature':
      return { primary: '#047857', secondary: '#34d399', accent: '#f59e0b', background: '#ffffff', surface: '#f0fdf4', text: '#1f2937', muted: '#6b7280' };
    case 'red-energetic':
      return { primary: '#dc2626', secondary: '#f87171', accent: '#fbbf24', background: '#ffffff', surface: '#fef2f2', text: '#1f2937', muted: '#6b7280' };
    case 'black-luxury':
      return { primary: '#111827', secondary: '#4b5563', accent: '#d4af37', background: '#ffffff', surface: '#f9fafb', text: '#111827', muted: '#6b7280' };
    case 'warm-neutral':
      return { primary: '#92400e', secondary: '#d97706', accent: '#0d9488', background: '#fffaf5', surface: '#fff7ed', text: '#431407', muted: '#78716c' };
    case 'high-contrast':
      return { primary: '#000000', secondary: '#374151', accent: '#2563eb', background: '#ffffff', surface: '#f3f4f6', text: '#000000', muted: '#4b5563' };
    case 'pastel-soft':
      return { primary: '#818cf8', secondary: '#f9a8d4', accent: '#fde047', background: '#fdfdfd', surface: '#f5f3ff', text: '#3730a3', muted: '#7c7c9c' };
    case 'custom':
    default:
      return null;
  }
}

function resolveColors(designSpec: DesignSpec): DesignSpec['colors'] {
  const preset = getColorSchemePreset(designSpec.colorScheme || 'custom');
  if (!preset) return designSpec.colors;
  return { ...preset, ...designSpec.colors };
}

function getScaleClass(scale: DesignSpec['layout']['spacing']): string {
  switch (scale) {
    case 'compact':
      return 'py-10 md:py-14 gap-8';
    case 'loose':
      return 'py-20 md:py-28 gap-16';
    case 'normal':
    default:
      return 'py-16 md:py-24 gap-12';
  }
}

function getButtonClasses(designSpec: DesignSpec): string {
  const base = 'inline-flex items-center justify-center px-6 py-3 font-semibold transition-all';
  const shadow = designSpec.components.buttons.shadow;
  const shadowClass = shadow === 'md' ? ' shadow-lg' : shadow === 'sm' ? ' shadow' : '';
  const radiusClass = designSpec.components.buttons.style === 'rounded' ? 'rounded-full' : 'rounded-[var(--pf-button-radius)]';

  switch (designSpec.components.buttons.style) {
    case 'outline':
      return `${base} border-2 border-[var(--pf-primary)] text-[var(--pf-primary)] hover:bg-[var(--pf-primary)] hover:text-white${shadowClass} ${radiusClass}`;
    case 'ghost':
      return `${base} text-[var(--pf-primary)] hover:bg-[var(--pf-primary)]/10${shadowClass} ${radiusClass}`;
    case 'rounded':
      return `${base} bg-[var(--pf-primary)] text-white hover:opacity-90${shadowClass} ${radiusClass}`;
    case 'solid':
    default:
      return `${base} bg-[var(--pf-primary)] text-white hover:opacity-90${shadowClass} ${radiusClass}`;
  }
}

function sectionWrapper(section: SectionSpec, content: string, extraClass = ''): string {
  const layoutClass = section.layout === 'full-width' ? '' : 'container mx-auto px-4';
  const containedClass = section.layout === 'full-width' ? '' : 'container mx-auto';
  const bgClass = section.type === 'hero' || section.type === 'cta' ? '' : 'bg-[var(--pf-background)] dark:bg-[var(--pf-dark-bg)]';
  return `
    <section id="${section.id}" class="${bgClass} ${extraClass}">
      <div class="${containedClass} ${layoutClass}">
        ${content}
      </div>
    </section>
  `;
}

function renderHero(section: SectionSpec, config: ProjectConfig, designSpec: DesignSpec): string {
  const content = section.content || {};
  const headline = String(content.headline || config.hero.titleLine1 || getBrandName(config));
  const description = String(content.description || config.hero.description || getBrandDescription(config));
  const cta = String(content.cta || config.hero.primaryButtonText || '立即了解');
  const tagline = String(content.tagline || config.hero.tagline || config.brand.tagline || '');

  const inner = `
    <div class="text-center py-20 md:py-32 text-white" style="background: linear-gradient(135deg, var(--pf-primary), var(--pf-secondary));">
      ${tagline ? `<p class="text-sm md:text-base uppercase tracking-widest mb-4 opacity-90">${escapeHtml(tagline)}</p>` : ''}
      <h1 class="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">${escapeHtml(headline)}</h1>
      <p class="text-base md:text-lg max-w-2xl mx-auto mb-8 opacity-90">${escapeHtml(description)}</p>
      <a href="#" class="${getButtonClasses({ ...designSpec, components: { ...designSpec.components, buttons: { ...designSpec.components.buttons, style: 'rounded' } } })}">${escapeHtml(cta)}</a>
    </div>
  `;
  return sectionWrapper(section, inner, 'overflow-hidden');
}

function renderFeatures(section: SectionSpec, config: ProjectConfig, designSpec: DesignSpec): string {
  const items = (section.content?.items as Array<{ title: string; description: string; icon?: string }>) || config.services.slice(0, 6).map((s) => ({ title: s.title, description: s.description, icon: s.icon }));
  if (!items.length) return '';
  const title = String(section.content?.title || '核心優勢');
  const subtitle = String(section.content?.subtitle || 'Why Choose Us');

  const inner = `
    <div class="${getScaleClass(designSpec.layout.spacing)}">
      <div class="text-center mb-12">
        <p class="text-sm uppercase tracking-widest mb-2 text-[var(--pf-primary)]">${escapeHtml(subtitle)}</p>
        <h2 class="text-2xl md:text-4xl font-bold text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(title)}</h2>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        ${items.map((item) => `
          <div class="p-6 rounded-lg bg-[var(--pf-surface)] dark:bg-[var(--pf-dark-surface)] border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow">
            <div class="w-12 h-12 rounded-lg mb-4 flex items-center justify-center text-white bg-[var(--pf-primary)]">
              <span class="text-xl">${escapeHtml(item.icon || '✦')}</span>
            </div>
            <h3 class="text-lg font-semibold mb-2 text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(item.title)}</h3>
            <p class="text-sm text-[var(--pf-muted)]">${escapeHtml(item.description)}</p>
          </div>
        `).join('')}
      </div>
    </div>
  `;
  return sectionWrapper(section, inner);
}

function renderServices(section: SectionSpec, config: ProjectConfig, designSpec: DesignSpec): string {
  const items = (section.content?.items as Array<{ title: string; description: string }>) || config.services.map((s) => ({ title: s.title, description: s.description }));
  if (!items.length) return '';
  const title = String(section.content?.title || '服務項目');

  const inner = `
    <div class="${getScaleClass(designSpec.layout.spacing)}">
      <div class="text-center mb-12">
        <p class="text-sm uppercase tracking-widest mb-2 text-[var(--pf-primary)]">Services</p>
        <h2 class="text-2xl md:text-4xl font-bold text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(title)}</h2>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        ${items.map((item, index) => `
          <div class="flex gap-4 p-5 rounded-lg bg-[var(--pf-surface)] dark:bg-[var(--pf-dark-surface)] border border-slate-200 dark:border-slate-700">
            <div class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white bg-[var(--pf-primary)] font-bold">${index + 1}</div>
            <div>
              <h3 class="text-lg font-semibold mb-1 text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(item.title)}</h3>
              <p class="text-sm text-[var(--pf-muted)]">${escapeHtml(item.description)}</p>
            </div>
          </div>
        `).join('')}
      </div>
    </div>
  `;
  return sectionWrapper(section, inner);
}

function renderTestimonials(section: SectionSpec, _config: ProjectConfig, designSpec: DesignSpec): string {
  const items = (section.content?.items as Array<{ quote: string; author: string; role: string }>) || [
    { quote: '服務非常專業，整個過程順利無憂。', author: '陳先生', role: '客戶' },
    { quote: '團隊反應迅速，提供的建議非常實用。', author: '林小姐', role: '客戶' },
    { quote: '強烈推薦給有需要的朋友。', author: '王女士', role: '客戶' },
  ];
  const title = String(section.content?.title || '客戶評價');

  const inner = `
    <div class="${getScaleClass(designSpec.layout.spacing)}">
      <div class="text-center mb-12">
        <p class="text-sm uppercase tracking-widest mb-2 text-[var(--pf-primary)]">Testimonials</p>
        <h2 class="text-2xl md:text-4xl font-bold text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(title)}</h2>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        ${items.map((item) => `
          <div class="p-6 rounded-lg bg-[var(--pf-surface)] dark:bg-[var(--pf-dark-surface)] border border-slate-200 dark:border-slate-700">
            <p class="text-lg italic mb-4 text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">"${escapeHtml(item.quote)}"</p>
            <div>
              <p class="font-semibold text-[var(--pf-primary)]">${escapeHtml(item.author)}</p>
              <p class="text-sm text-[var(--pf-muted)]">${escapeHtml(item.role)}</p>
            </div>
          </div>
        `).join('')}
      </div>
    </div>
  `;
  return sectionWrapper(section, inner);
}

function renderPricing(section: SectionSpec, _config: ProjectConfig, designSpec: DesignSpec): string {
  const plans = (section.content?.plans as Array<{ name: string; price: string; period: string; features: string[]; highlighted?: boolean }>) || [
    { name: 'Basic', price: '$99', period: '/月', features: ['核心功能', '電郵支援', '基本報表'], highlighted: false },
    { name: 'Pro', price: '$199', period: '/月', features: ['全部功能', '優先支援', '進階報表', 'API 存取'], highlighted: true },
    { name: 'Enterprise', price: '客製', period: '', features: ['專屬方案', '專人對接', 'SLA 保障', '自訂整合'], highlighted: false },
  ];
  const title = String(section.content?.title || '價格方案');

  const inner = `
    <div class="${getScaleClass(designSpec.layout.spacing)}">
      <div class="text-center mb-12">
        <p class="text-sm uppercase tracking-widest mb-2 text-[var(--pf-primary)]">Pricing</p>
        <h2 class="text-2xl md:text-4xl font-bold text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(title)}</h2>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
        ${plans.map((plan) => `
          <div class="p-6 rounded-lg border ${plan.highlighted ? 'border-[var(--pf-primary)] ring-2 ring-[var(--pf-primary)]' : 'border-slate-200 dark:border-slate-700'} bg-[var(--pf-surface)] dark:bg-[var(--pf-dark-surface)]">
            <h3 class="text-lg font-semibold mb-2 text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(plan.name)}</h3>
            <div class="text-3xl font-bold mb-4 text-[var(--pf-primary)]">${escapeHtml(plan.price)}<span class="text-base font-normal text-[var(--pf-muted)]">${escapeHtml(plan.period)}</span></div>
            <ul class="space-y-2 mb-6">
              ${(plan.features || []).map((feature) => `<li class="text-sm text-[var(--pf-muted)] flex items-center gap-2"><span class="text-[var(--pf-accent)]">✓</span> ${escapeHtml(feature)}</li>`).join('')}
            </ul>
            <button type="button" class="w-full ${getButtonClasses(designSpec)}">選擇方案</button>
          </div>
        `).join('')}
      </div>
    </div>
  `;
  return sectionWrapper(section, inner);
}

function renderAbout(section: SectionSpec, config: ProjectConfig, designSpec: DesignSpec): string {
  const title = String(section.content?.title || `關於${getBrandName(config)}`);
  const description = String(section.content?.description || getBrandDescription(config) || `${getBrandName(config)} 致力於提供專業、可靠的服務。`);

  const inner = `
    <div class="${getScaleClass(designSpec.layout.spacing)}">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div class="h-64 md:h-80 rounded-lg bg-[var(--pf-surface)] dark:bg-[var(--pf-dark-surface)] flex items-center justify-center text-[var(--pf-muted)] border border-slate-200 dark:border-slate-700">
          公司圖片
        </div>
        <div>
          <p class="text-sm uppercase tracking-widest mb-2 text-[var(--pf-primary)]">About Us</p>
          <h2 class="text-2xl md:text-4xl font-bold mb-4 text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(title)}</h2>
          <p class="text-[var(--pf-muted)] mb-6">${escapeHtml(description)}</p>
          <div class="flex flex-wrap gap-2">
            ${['專業', '可靠', '創新', '客製化'].map((tag) => `<span class="px-3 py-1 text-sm rounded-full border border-slate-200 dark:border-slate-700 text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(tag)}</span>`).join('')}
          </div>
        </div>
      </div>
    </div>
  `;
  return sectionWrapper(section, inner);
}

function renderContact(section: SectionSpec, config: ProjectConfig, designSpec: DesignSpec): string {
  const title = String(section.content?.title || '聯絡我們');
  const inner = `
    <div class="${getScaleClass(designSpec.layout.spacing)}">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div>
          <p class="text-sm uppercase tracking-widest mb-2 text-[var(--pf-primary)]">Contact</p>
          <h2 class="text-2xl md:text-4xl font-bold mb-6 text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(title)}</h2>
          <div class="space-y-4 text-[var(--pf-muted)]">
            <p>電話：<a href="tel:${escapeHtml(config.contact.phone)}" class="underline text-[var(--pf-primary)]">${escapeHtml(config.contact.phoneDisplay || config.contact.phone)}</a></p>
            <p>電郵：<a href="mailto:${escapeHtml(config.contact.email)}" class="underline text-[var(--pf-primary)]">${escapeHtml(config.contact.email)}</a></p>
            <p>地址：${escapeHtml(config.contact.addressDisplay || config.contact.address)}</p>
          </div>
        </div>
        <form class="p-6 rounded-lg bg-[var(--pf-surface)] dark:bg-[var(--pf-dark-surface)] border border-slate-200 dark:border-slate-700 space-y-4" onsubmit="return false;">
          <div>
            <label class="block text-sm font-medium mb-1 text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">姓名</label>
            <input type="text" disabled class="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800" />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1 text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">電郵</label>
            <input type="email" disabled class="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800" />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1 text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">訊息</label>
            <textarea disabled rows="4" class="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"></textarea>
          </div>
          <button type="button" disabled class="w-full ${getButtonClasses(designSpec)} disabled:opacity-50">送出</button>
        </form>
      </div>
    </div>
  `;
  return sectionWrapper(section, inner);
}

function renderCta(section: SectionSpec, _config: ProjectConfig, designSpec: DesignSpec): string {
  const title = String(section.content?.title || '準備好開始了嗎？');
  const description = String(section.content?.description || '立即聯絡我們，獲取專業諮詢。');
  const inner = `
    <div class="${getScaleClass(designSpec.layout.spacing)} text-center text-white rounded-2xl" style="background: linear-gradient(135deg, var(--pf-primary), var(--pf-secondary));">
      <h2 class="text-2xl md:text-4xl font-bold mb-4">${escapeHtml(title)}</h2>
      <p class="text-base md:text-lg max-w-2xl mx-auto mb-8 opacity-90">${escapeHtml(description)}</p>
      <a href="#contact" class="${getButtonClasses({ ...designSpec, components: { ...designSpec.components, buttons: { ...designSpec.components.buttons, style: 'rounded' } } })}">立即聯絡</a>
    </div>
  `;
  return sectionWrapper(section, inner);
}

function renderFaq(section: SectionSpec, _config: ProjectConfig, designSpec: DesignSpec): string {
  const items = (section.content?.items as Array<{ question: string; answer: string }>) || [
    { question: '如何開始使用？', answer: '請填寫聯絡表格或致電我們，我們會盡快回覆。' },
    { question: '提供哪些付款方式？', answer: '我們支援銀行轉帳、信用卡及常見電子支付方式。' },
    { question: '是否提供售後支援？', answer: '是的，我們提供完整的售後支援與維護服務。' },
  ];
  const title = String(section.content?.title || '常見問題');

  const inner = `
    <div class="${getScaleClass(designSpec.layout.spacing)}">
      <div class="text-center mb-12">
        <p class="text-sm uppercase tracking-widest mb-2 text-[var(--pf-primary)]">FAQ</p>
        <h2 class="text-2xl md:text-4xl font-bold text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(title)}</h2>
      </div>
      <div class="max-w-3xl mx-auto space-y-4">
        ${items.map((item, index) => `
          <details class="group p-4 rounded-lg bg-[var(--pf-surface)] dark:bg-[var(--pf-dark-surface)] border border-slate-200 dark:border-slate-700" ${index === 0 ? 'open' : ''}>
            <summary class="flex justify-between items-center font-semibold cursor-pointer text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">
              ${escapeHtml(item.question)}
              <span class="transition-transform group-open:rotate-180">▼</span>
            </summary>
            <p class="mt-3 text-[var(--pf-muted)]">${escapeHtml(item.answer)}</p>
          </details>
        `).join('')}
      </div>
    </div>
  `;
  return sectionWrapper(section, inner);
}

function renderGallery(section: SectionSpec, _config: ProjectConfig, designSpec: DesignSpec): string {
  const title = String(section.content?.title || '作品展示');
  const items = (section.content?.items as Array<{ title: string; category: string }>) || [
    { title: 'Project A', category: '作品' },
    { title: 'Project B', category: '作品' },
    { title: 'Project C', category: '作品' },
    { title: 'Project D', category: '作品' },
  ];

  const inner = `
    <div class="${getScaleClass(designSpec.layout.spacing)}">
      <div class="text-center mb-12">
        <p class="text-sm uppercase tracking-widest mb-2 text-[var(--pf-primary)]">Gallery</p>
        <h2 class="text-2xl md:text-4xl font-bold text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(title)}</h2>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        ${items.map((item) => `
          <div class="aspect-square rounded-lg bg-[var(--pf-surface)] dark:bg-[var(--pf-dark-surface)] border border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center text-center p-4">
            <div class="text-4xl mb-2 text-[var(--pf-primary)]">◆</div>
            <h3 class="font-semibold text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(item.title)}</h3>
            <p class="text-xs text-[var(--pf-muted)]">${escapeHtml(item.category)}</p>
          </div>
        `).join('')}
      </div>
    </div>
  `;
  return sectionWrapper(section, inner);
}

function renderPortfolio(section: SectionSpec, config: ProjectConfig, designSpec: DesignSpec): string {
  return renderGallery({ ...section, type: 'gallery' }, config, designSpec);
}

function renderTeam(section: SectionSpec, config: ProjectConfig, designSpec: DesignSpec): string {
  const items = (section.content?.items as Array<{ name: string; title: string; photo?: string }>) || config.team.map((m) => ({ name: m.name, title: m.title, photo: m.photo }));
  if (!items.length) return '';
  const title = String(section.content?.title || '專業團隊');

  const inner = `
    <div class="${getScaleClass(designSpec.layout.spacing)}">
      <div class="text-center mb-12">
        <p class="text-sm uppercase tracking-widest mb-2 text-[var(--pf-primary)]">Team</p>
        <h2 class="text-2xl md:text-4xl font-bold text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(title)}</h2>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        ${items.map((member) => `
          <div class="text-center p-4 rounded-lg bg-[var(--pf-surface)] dark:bg-[var(--pf-dark-surface)] border border-slate-200 dark:border-slate-700">
            <div class="w-20 h-20 mx-auto rounded-full bg-slate-200 dark:bg-slate-700 mb-3 flex items-center justify-center text-[var(--pf-muted)] overflow-hidden">
              ${member.photo ? `<img src="${escapeHtml(member.photo)}" alt="" class="w-full h-full object-cover" />` : '頭像'}
            </div>
            <h3 class="font-semibold text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(member.name)}</h3>
            <p class="text-sm text-[var(--pf-muted)]">${escapeHtml(member.title)}</p>
          </div>
        `).join('')}
      </div>
    </div>
  `;
  return sectionWrapper(section, inner);
}

function renderHeader(config: ProjectConfig, sections: SectionSpec[]): string {
  const brandName = getBrandName(config);
  const navLinks = sections
    .filter((s) => s.visible && s.type !== 'footer')
    .map((s) => ({ label: s.name, href: `#${s.id}` }));

  return `
    <header class="sticky top-0 z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur shadow-sm">
      <nav class="container mx-auto px-4 py-4 flex justify-between items-center">
        <a href="#" class="text-xl font-bold" style="color: var(--pf-primary)">${escapeHtml(brandName)}</a>
        <div class="hidden md:flex gap-6 text-sm">
          ${navLinks.map((item) => `<a href="${escapeHtml(item.href)}" class="hover:opacity-80">${escapeHtml(item.label)}</a>`).join('')}
        </div>
        <button class="md:hidden p-2 rounded" style="background: var(--pf-primary); color: white">☰</button>
      </nav>
    </header>
  `;
}

function renderDefaultFooter(config: ProjectConfig): string {
  const brandName = getBrandName(config);
  return `
    <footer class="py-10 text-white" style="background: var(--pf-dark-bg, #0f172a);">
      <div class="container mx-auto px-4 text-center">
        <h3 class="text-lg font-bold mb-2">${escapeHtml(brandName)}</h3>
        <p class="text-sm opacity-80 mb-4">${escapeHtml(getBrandDescription(config).slice(0, 120))}${getBrandDescription(config).length > 120 ? '...' : ''}</p>
        <div class="border-t border-white/20 pt-6 text-sm opacity-70">
          © ${new Date().getFullYear()} ${escapeHtml(brandName)}. All rights reserved.
        </div>
      </div>
    </footer>
  `;
}

function renderFooter(section: SectionSpec, config: ProjectConfig): string {
  const brandName = getBrandName(config);
  const inner = `
    <div class="py-10 text-white" style="background: var(--pf-dark-bg, #0f172a);">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        <div>
          <h3 class="text-lg font-bold mb-2">${escapeHtml(brandName)}</h3>
          <p class="text-sm opacity-80">${escapeHtml(getBrandDescription(config).slice(0, 120))}${getBrandDescription(config).length > 120 ? '...' : ''}</p>
        </div>
        <div>
          <h4 class="font-semibold mb-2">聯絡資訊</h4>
          <p class="text-sm opacity-80">${escapeHtml(config.contact.phoneDisplay || config.contact.phone)}</p>
          <p class="text-sm opacity-80">${escapeHtml(config.contact.email)}</p>
        </div>
        <div>
          <h4 class="font-semibold mb-2">快速連結</h4>
          <ul class="space-y-1 text-sm opacity-80">
            <li><a href="#" class="hover:underline">首頁</a></li>
            <li><a href="#about" class="hover:underline">關於我們</a></li>
            <li><a href="#contact" class="hover:underline">聯絡我們</a></li>
          </ul>
        </div>
      </div>
      <div class="border-t border-white/20 pt-6 text-center text-sm opacity-70">
        © ${new Date().getFullYear()} ${escapeHtml(brandName)}. All rights reserved.
      </div>
    </div>
  `;
  return sectionWrapper({ ...section, layout: 'full-width' }, inner);
}

function renderStats(section: SectionSpec, _config: ProjectConfig, designSpec: DesignSpec): string {
  const items = (section.content?.items as Array<{ label: string; value: string }>) || [
    { label: '年經驗', value: '10+' },
    { label: '客戶', value: '500+' },
    { label: '項目', value: '1200+' },
    { label: '滿意度', value: '99%' },
  ];
  const title = String(section.content?.title || '數據成就');

  const inner = `
    <div class="${getScaleClass(designSpec.layout.spacing)} text-center">
      <h2 class="text-2xl md:text-4xl font-bold mb-12 text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(title)}</h2>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
        ${items.map((item) => `
          <div class="p-4">
            <div class="text-3xl md:text-4xl font-bold text-[var(--pf-primary)]">${escapeHtml(item.value)}</div>
            <div class="text-sm text-[var(--pf-muted)]">${escapeHtml(item.label)}</div>
          </div>
        `).join('')}
      </div>
    </div>
  `;
  return sectionWrapper(section, inner);
}

function renderProcess(section: SectionSpec, _config: ProjectConfig, designSpec: DesignSpec): string {
  const items = (section.content?.items as Array<{ title: string; description: string }>) || [
    { title: '諮詢', description: '了解您的需求與目標。' },
    { title: '規劃', description: '制定最適合的解決方案。' },
    { title: '執行', description: '專業團隊高效執行。' },
    { title: '交付', description: '準時交付並提供支援。' },
  ];
  const title = String(section.content?.title || '服務流程');

  const inner = `
    <div class="${getScaleClass(designSpec.layout.spacing)}">
      <div class="text-center mb-12">
        <p class="text-sm uppercase tracking-widest mb-2 text-[var(--pf-primary)]">Process</p>
        <h2 class="text-2xl md:text-4xl font-bold text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(title)}</h2>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
        ${items.map((item, index) => `
          <div class="relative p-5 rounded-lg bg-[var(--pf-surface)] dark:bg-[var(--pf-dark-surface)] border border-slate-200 dark:border-slate-700 text-center">
            <div class="w-10 h-10 mx-auto mb-3 rounded-full bg-[var(--pf-primary)] text-white flex items-center justify-center font-bold">${index + 1}</div>
            <h3 class="font-semibold mb-1 text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(item.title)}</h3>
            <p class="text-sm text-[var(--pf-muted)]">${escapeHtml(item.description)}</p>
          </div>
        `).join('')}
      </div>
    </div>
  `;
  return sectionWrapper(section, inner);
}

function renderTrustLogos(section: SectionSpec, _config: ProjectConfig, designSpec: DesignSpec): string {
  const title = String(section.content?.title || '值得信賴');
  const logos = (section.content?.logos as string[]) || ['Partner A', 'Partner B', 'Partner C', 'Partner D', 'Partner E'];

  const inner = `
    <div class="${getScaleClass(designSpec.layout.spacing)} text-center">
      <p class="text-sm uppercase tracking-widest mb-6 text-[var(--pf-primary)]">${escapeHtml(title)}</p>
      <div class="flex flex-wrap justify-center items-center gap-8 opacity-70">
        ${logos.map((logo) => `<div class="px-4 py-2 rounded border border-slate-200 dark:border-slate-700 text-[var(--pf-muted)]">${escapeHtml(logo)}</div>`).join('')}
      </div>
    </div>
  `;
  return sectionWrapper(section, inner);
}

function renderNewsletter(section: SectionSpec, _config: ProjectConfig, designSpec: DesignSpec): string {
  const title = String(section.content?.title || '訂閱電子報');
  const description = String(section.content?.description || '獲取最新資訊與優惠。');

  const inner = `
    <div class="${getScaleClass(designSpec.layout.spacing)} max-w-2xl mx-auto text-center">
      <h2 class="text-2xl md:text-4xl font-bold mb-4 text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(title)}</h2>
      <p class="text-[var(--pf-muted)] mb-6">${escapeHtml(description)}</p>
      <form class="flex flex-col sm:flex-row gap-3" onsubmit="return false;">
        <input type="email" disabled placeholder="your@email.com" class="flex-1 px-4 py-3 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800" />
        <button type="button" disabled class="${getButtonClasses(designSpec)} disabled:opacity-50">訂閱</button>
      </form>
    </div>
  `;
  return sectionWrapper(section, inner);
}

function renderBlog(section: SectionSpec, config: ProjectConfig, designSpec: DesignSpec): string {
  const items = (section.content?.items as Array<{ title: string; summary: string; date: string }>) || (config.news ?? []).slice(0, 3).map((n) => ({ title: n.title, summary: n.summary, date: n.date }));
  if (!items.length) return '';
  const title = String(section.content?.title || '最新文章');

  const inner = `
    <div class="${getScaleClass(designSpec.layout.spacing)}">
      <div class="text-center mb-12">
        <p class="text-sm uppercase tracking-widest mb-2 text-[var(--pf-primary)]">Blog</p>
        <h2 class="text-2xl md:text-4xl font-bold text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(title)}</h2>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        ${items.map((item) => `
          <div class="p-5 rounded-lg bg-[var(--pf-surface)] dark:bg-[var(--pf-dark-surface)] border border-slate-200 dark:border-slate-700">
            <p class="text-xs text-[var(--pf-muted)] mb-2">${escapeHtml(item.date)}</p>
            <h3 class="text-lg font-semibold mb-2 text-[var(--pf-text)] dark:text-[var(--pf-dark-text)]">${escapeHtml(item.title)}</h3>
            <p class="text-sm text-[var(--pf-muted)]">${escapeHtml(item.summary)}</p>
          </div>
        `).join('')}
      </div>
    </div>
  `;
  return sectionWrapper(section, inner);
}

const sectionRenderers: Record<SectionType, (section: SectionSpec, config: ProjectConfig, designSpec: DesignSpec) => string> = {
  hero: renderHero,
  features: renderFeatures,
  services: renderServices,
  testimonials: renderTestimonials,
  pricing: renderPricing,
  about: renderAbout,
  contact: renderContact,
  cta: renderCta,
  faq: renderFaq,
  portfolio: renderPortfolio,
  gallery: renderGallery,
  team: renderTeam,
  footer: renderFooter,
  stats: renderStats,
  process: renderProcess,
  'trust-logos': renderTrustLogos,
  newsletter: renderNewsletter,
  blog: renderBlog,
};

function normalizeDesignSpec(designSpec: DesignSpec): DesignSpec {
  const defaults: Partial<DesignSpec> = {
    colorScheme: 'custom',
    fontPair: 'system',
    layoutStyle: 'centered-single',
    colors: {
      primary: '#1a56db',
      secondary: '#60a5fa',
      accent: '#f59e0b',
      background: '#ffffff',
      surface: '#f8fafc',
      text: '#1f2937',
      muted: '#6b7280',
    },
    typography: {
      headingFont: 'system-ui',
      bodyFont: 'system-ui',
      baseSize: '16px',
      scale: 'medium',
    },
    layout: {
      maxWidth: '1280px',
      gridColumns: 12,
      spacing: 'normal',
      containerPadding: '1rem',
    },
    components: {
      buttons: {
        style: 'solid',
        radius: '0.5rem',
        shadow: 'none',
      },
      cards: {
        style: 'flat',
        radius: '0.5rem',
      },
      forms: {
        style: 'boxed',
      },
    },
    animations: {
      pageLoad: 'none',
      scrollReveal: 'none',
      hover: 'none',
      transition: 'none',
    },
    sections: [],
  };

  return {
    ...defaults,
    ...designSpec,
    colors: { ...(defaults.colors as DesignSpec['colors']), ...designSpec.colors },
    typography: { ...(defaults.typography as DesignSpec['typography']), ...designSpec.typography },
    layout: { ...(defaults.layout as DesignSpec['layout']), ...designSpec.layout },
    components: {
      buttons: { ...(defaults.components!.buttons), ...designSpec.components?.buttons },
      cards: { ...(defaults.components!.cards), ...designSpec.components?.cards },
      forms: { ...(defaults.components!.forms), ...designSpec.components?.forms },
    },
    animations: { ...(defaults.animations as DesignSpec['animations']), ...designSpec.animations },
    sections: designSpec.sections ?? [],
  } as DesignSpec;
}

export function generateMockupFromDesignSpec(designSpec: DesignSpec, config: ProjectConfig): string {
  const spec = normalizeDesignSpec(designSpec);
  const colors = resolveColors(spec);
  const isDark = config.design.darkMode;
  const fontPair: FontPair = spec.fontPair || 'system';
  const { imports, family } = getFontConfig(fontPair, spec);
  const maxWidth = spec.layout.maxWidth || '1280px';
  const buttonRadius = spec.components.buttons.radius || '0.5rem';

  const visibleSections = (spec.sections ?? [])
    .filter((s) => s.visible)
    .sort((a, b) => a.order - b.order);

  const hasFooter = visibleSections.some((s) => s.type === 'footer');

  const bodyContent = visibleSections
    .map((section) => {
      const renderer = sectionRenderers[section.type];
      return renderer ? renderer(section, config, designSpec) : '';
    })
    .join('\n');

  return `<!DOCTYPE html>
<html lang="${config.language}" class="${isDark ? 'dark' : ''}">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(config.displayName)} — Mockup</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  ${imports}
  <style>
    :root {
      --pf-primary: ${colors.primary};
      --pf-secondary: ${colors.secondary};
      --pf-accent: ${colors.accent};
      --pf-background: ${colors.background};
      --pf-surface: ${colors.surface};
      --pf-text: ${colors.text};
      --pf-muted: ${colors.muted};
      --pf-dark-bg: ${colors.darkMode?.background ?? '#0f172a'};
      --pf-dark-surface: ${colors.darkMode?.surface ?? '#1e293b'};
      --pf-dark-text: ${colors.darkMode?.text ?? '#f8fafc'};
      --pf-button-radius: ${buttonRadius};
    }
    body { font-family: ${family}; }
    .container { max-width: ${maxWidth}; }
  </style>
  <script>
    tailwind.config = { darkMode: 'class' };
  </script>
</head>
<body class="bg-[var(--pf-background)] text-[var(--pf-text)] min-h-screen">
  ${renderHeader(config, visibleSections)}
  ${bodyContent}
  ${hasFooter ? '' : renderDefaultFooter(config)}
  <div class="fixed bottom-4 right-4 z-[9999] px-4 py-2 rounded-full text-xs font-semibold shadow-lg" style="background: var(--pf-dark-bg, #0f172a); color: white; opacity: 0.85;">
    Mockup 預覽 — 非最終網站
  </div>
</body>
</html>`;
}
